import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import Navbar from "@/components/Navbar";
import { Play, Clock, ArrowLeft } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)} min`;

export default function ImmersionEnvironments() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [envs, setEnvs] = useState([]);
  const [topic, setTopic] = useState(null);

  useEffect(() => {
    api.get("/immersions").then((r) => setEnvs(r.data)).catch(() => {});
    api.get("/topics").then((r) => setTopic(r.data.find((t) => t.id === topicId))).catch(() => {});
  }, [topicId]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/immersions")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="env-back">
          <ArrowLeft className="w-4 h-4" /> Topics
        </button>
        <p className="text-xs uppercase tracking-[0.25em] text-primary mb-2">{topic?.title || "Immersion"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl mb-3">Choose your environment.</h1>
        <p className="text-muted-foreground max-w-xl mb-10 leading-relaxed">Where would you like to install <span className="text-foreground">{topic?.title?.toLowerCase()}</span>? Headphones on, somewhere you won't be disturbed.</p>
        <div className="grid sm:grid-cols-2 gap-5" data-testid="environment-grid">
          {envs.map((im, i) => (
            <motion.button key={im.id} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              onClick={() => navigate(`/immersions/${topicId}/${im.id}`)} data-testid={`env-card-${im.id}`}
              className="group relative overflow-hidden rounded-3xl aspect-[16/10] text-left ring-1 ring-border hover:ring-primary/40 transition-all duration-500">
              <img src={im.poster} alt={im.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className="flex items-center gap-2 text-white/70 text-xs mb-1"><Clock className="w-3 h-3" /> {fmt(im.duration)} · {im.theme}</div>
                <div className="font-serif text-3xl text-white leading-tight">{im.title}</div>
                <div className="text-white/80 text-sm">{im.subtitle}</div>
              </div>
              <div className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Play className="w-5 h-5 ml-0.5" />
              </div>
            </motion.button>
          ))}
        </div>
      </main>
    </div>
  );
}
